import {
  isPlatformBrowser,
  Location,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  effect,
  inject,
  PLATFORM_ID
} from '@angular/core';
import {
  Router,
  RouterOutlet,
} from '@angular/router';

import {
  App,
  URLOpenListenerEvent
} from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { QuickEditProfileComponent } from './account/quick-edit-profile/quick-edit-profile.component';
import { DrawerService } from './drawer/drawer.service';
import { emulateClick } from './core/a11y';
import { handleUrlOpen } from './core/url-open';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { NotificationService } from './notification/notification.service';
import { AuthService } from './shared/services/auth.service';
import { CanonicalService } from './shared/services/canonical.service';
import { CenterService } from './shared/services/center.service';
import { LoaderService } from './shared/services/loader.service';
import { UpdateService } from './shared/services/update.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    NgOptimizedImage,
  ],
  templateUrl: './app.component.html'
})
export class AppComponent {
  router = inject(Router);
  location = inject(Location);
  protected readonly Capacitor = Capacitor;
  protected readonly emulateClick = emulateClick;
  private platformId = inject(PLATFORM_ID);
  private notification = inject(NotificationService);
  private centerService = inject(CenterService);
  private loader = inject(LoaderService);
  private authService = inject(AuthService);
  private canonical = inject(CanonicalService);
  private drawerService = inject(DrawerService);
  private updateService = inject(UpdateService);

  private autoAssigning = false;
  private autoAssignFailed = false;
  private autoAssignDone = false;
  private simplifiedDrawerOpened = false;

  constructor() {
    this.initializeApp();
    this.checkUpdate().catch(err => console.error(err));

    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        const user = this.authService.user();
        const center = this.centerService.center();

        if (!user || !user.uid || !user.authCompleted || !user.erpId) {
          this.simplifiedDrawerOpened = false;
          this.autoAssignFailed = false;
          this.autoAssignDone = false;
          return;
        }

        // Auto-assign silently on city site
        if (user.shouldUpdateCenter && center && !this.autoAssigning && !this.autoAssignFailed && !this.autoAssignDone) {
          this.autoAssigning = true;
          this.autoAssignCenter(center.erpId).finally(() => {
            this.autoAssigning = false;
          });
          return;
        }

        // Open simplified drawer if profile is incomplete
        if (!this.simplifiedDrawerOpened && (user.shouldUpdateCenter || user.shouldUpdateConsent)) {
          this.simplifiedDrawerOpened = true;
          this.drawerService.open({
            component: QuickEditProfileComponent,
            inputs: { simplified: true },
            style: 'transparent',
            unclosable: true,
          });
        }
      });
    }
  }

  async checkUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    await this.updateService.update()
  }

  initializeApp(): void {
    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        handleUrlOpen(event.url, {
          ready: () => this.loader.initData(),
          centers: () => this.centerService.centers(),
          selectCenter: center => this.centerService.center.set(center),
          navigate: url => this.router.navigateByUrl(url),
          onNavigationError: err => this.notification.open({ type: 'error', title: 'error', message: (err as Error).message }),
          openBrowser: url => void Browser.open({ url }),
          closeBrowser: () => void Browser.close()
        }).catch(err => console.error(err));
      });
    }
  }

  private async autoAssignCenter(centerErpId: number): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    try {
      await this.authService.updateProfile({
        simplified: true,
        centerErpId,
        notification: user.notification ?? [],
        shouldUpdateCenter: true,
        shouldUpdateConsent: user.shouldUpdateConsent ?? true,
      });
      await this.authService.getIdToken(true);
      this.autoAssignDone = true;
    } catch (err) {
      this.autoAssignFailed = true;
      this.notification.open({ type: 'error', title: 'error', message: (err as Error).message });
    }
  }
}
